import {
  WebSocketMessage,
  AuthenticationRequest,
  ConnectionStatus,
  LogType,
  LogEntry,
  ClientStats,
} from '../types';
import { environment } from '../environments';

export class WinSignageWebSocketClient {
  private static readonly DEFAULT_SERVER_URL = 'ws://localhost:7000';
  private static readonly MAX_RECONNECT_DELAY_MS = 60000;

  private ws: WebSocket | null = null;
  private serverUrl: string = WinSignageWebSocketClient.DEFAULT_SERVER_URL;
  private clientId: string = '';
  private clientSecret: string = '';

  // Reconnection state
  private manualDisconnect: boolean = false;
  private reconnectAttempts: number = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private status: ConnectionStatus = ConnectionStatus.Disconnected;
  private stats: ClientStats = {
    connectionTime: null,
    messagesSent: 0,
    messagesReceived: 0,
    lastPing: null,
  };
  private logs: LogEntry[] = [];

  // Event callbacks
  private onStatusChange?: (status: ConnectionStatus) => void;
  private onLogAdded?: (log: LogEntry) => void;
  private onStatsUpdated?: (stats: ClientStats) => void;
  private onMessage?: (message: WebSocketMessage) => void;
  private getPlaybackStats?: () => any;

  constructor() {
    this.resetStats();
  }

  public setCallbacks(callbacks: {
    onStatusChange?: (status: ConnectionStatus) => void;
    onLogAdded?: (log: LogEntry) => void;
    onStatsUpdated?: (stats: ClientStats) => void;
    onMessage?: (message: WebSocketMessage) => void;
    getPlaybackStats?: () => any;
  }): void {
    this.onStatusChange = callbacks.onStatusChange;
    this.onLogAdded = callbacks.onLogAdded;
    this.onStatsUpdated = callbacks.onStatsUpdated;
    this.onMessage = callbacks.onMessage;
    this.getPlaybackStats = callbacks.getPlaybackStats;
  }

  public async connect(
    serverUrl: string = WinSignageWebSocketClient.DEFAULT_SERVER_URL,
    clientId: string,
    clientSecret: string
  ): Promise<void> {
    // Clean up any existing connection first
    if (this.ws) {
      // If connecting or open, can't connect again
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.addLog(LogType.Error, 'Already connected or connecting');
        return;
      }
      // If closing or closed, clean it up
      if (this.ws.readyState === WebSocket.CLOSING || this.ws.readyState === WebSocket.CLOSED) {
        this.ws = null;
      }
    }

    this.serverUrl = serverUrl;
    this.clientId = clientId;
    this.clientSecret = clientSecret;

    // A fresh user-initiated connect resets any reconnection back-off.
    this.manualDisconnect = false;
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();

    this.openConnection();
  }

  private openConnection(): void {
    this.updateStatus(ConnectionStatus.Connecting);
    this.addLog(LogType.Info, `Connecting to ${this.serverUrl}...`);

    try {
      this.ws = new WebSocket(this.serverUrl);
      this.setupWebSocketHandlers();
    } catch (error) {
      this.addLog(LogType.Error, `Connection failed: ${error}`);
      this.updateStatus(ConnectionStatus.Disconnected);
      // A synchronous constructor failure still warrants a retry attempt.
      this.scheduleReconnect();
    }
  }

  public disconnect(): void {
    // User-initiated disconnect: stop any pending/automatic reconnection.
    this.manualDisconnect = true;
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();

    if (this.ws) {
      this.addLog(LogType.Info, 'Disconnecting...');
      // Close the connection if it's open or connecting
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }
    // Always update status to disconnected
    this.updateStatus(ConnectionStatus.Disconnected);
    this.stats.connectionTime = null;
    this.notifyStatsUpdated();
  }

  public getStatus(): ConnectionStatus {
    return this.status;
  }

  public getStats(): ClientStats {
    return { ...this.stats };
  }

  public getLogs(): LogEntry[] {
    return [...this.logs];
  }

  public clearLogs(): void {
    this.logs = [];
  }

  public exportLogs(): string {
    return this.logs
      .map((log) => `[${log.timestamp.toISOString()}] [${log.type.toUpperCase()}] ${log.message}`)
      .join('\n');
  }

  public static getDefaultServerUrl(): string {
    return WinSignageWebSocketClient.DEFAULT_SERVER_URL;
  }

  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.addLog(LogType.Info, 'WebSocket connection established');
      this.updateStatus(ConnectionStatus.Authenticating);
      this.stats.connectionTime = new Date();
      this.notifyStatsUpdated();
      this.sendAuthentication();
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.stats.messagesReceived++;
        this.notifyStatsUpdated();
        this.addLog(LogType.Received, `Message: ${message.Type}`);
        this.handleMessage(message);
      } catch (error) {
        this.addLog(LogType.Error, `Failed to parse message: ${error}`);
      }
    };

    this.ws.onerror = (error) => {
      this.addLog(LogType.Error, `WebSocket error: ${error}`);
    };

    this.ws.onclose = (event) => {
      this.addLog(LogType.Info, `Connection closed (Code: ${event.code})`);
      // Only clear ws if this is the same instance that closed
      const closedCurrentConnection = this.ws === event.target;
      if (closedCurrentConnection) {
        this.ws = null;
        // Update status to disconnected since our current connection closed
        this.updateStatus(ConnectionStatus.Disconnected);
        this.stats.connectionTime = null;
        this.notifyStatsUpdated();

        // Automatically reconnect on unexpected drops (network loss, server restart).
        // Digital signage must recover on its own without an operator on site.
        if (!this.manualDisconnect) {
          this.scheduleReconnect();
        }
      }
    };
  }

  private scheduleReconnect(): void {
    if (this.manualDisconnect) {
      return;
    }

    // Avoid stacking multiple pending reconnect attempts.
    if (this.reconnectTimer !== null) {
      return;
    }

    if (this.reconnectAttempts >= environment.maxReconnectAttempts) {
      this.addLog(
        LogType.Error,
        `Giving up reconnection after ${this.reconnectAttempts} attempts. Click Connect to retry.`
      );
      return;
    }

    // Exponential back-off with a small jitter, capped at MAX_RECONNECT_DELAY_MS.
    const base = environment.reconnectInterval * Math.pow(2, this.reconnectAttempts);
    const capped = Math.min(base, WinSignageWebSocketClient.MAX_RECONNECT_DELAY_MS);
    const jitter = Math.floor((this.reconnectAttempts % 3) * 250);
    const delay = capped + jitter;

    this.reconnectAttempts++;
    this.addLog(
      LogType.Info,
      `Reconnecting in ${Math.round(delay / 1000)}s (attempt ${this.reconnectAttempts}/${environment.maxReconnectAttempts})...`
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.manualDisconnect) {
        this.openConnection();
      }
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private sendAuthentication(): void {
    const authRequest: AuthenticationRequest = {
      ClientId: this.clientId,
      ClientSecret: this.clientSecret,
    };

    this.sendMessage(JSON.stringify(authRequest));
    this.addLog(LogType.Sent, `Authentication request sent for client: ${this.clientId}`);
  }

  private handleMessage(message: WebSocketMessage): void {
    if (this.onMessage) {
      this.onMessage(message);
    }

    switch (message.Type) {
      case 'auth_success':
        this.updateStatus(ConnectionStatus.Connected);
        this.addLog(LogType.Info, '? Authentication successful!');
        // Connection is healthy again: reset the back-off counter.
        this.reconnectAttempts = 0;
        break;

      case 'auth_failed':
        this.updateStatus(ConnectionStatus.Disconnected);
        this.addLog(LogType.Error, '? Authentication failed!');
        // Bad credentials will not fix themselves — stop auto-reconnecting immediately
        // so a server-initiated close does not trigger a reconnect with the same secret.
        this.manualDisconnect = true;
        this.clearReconnectTimer();
        setTimeout(() => this.disconnect(), 2000);
        break;

      case 'ping':
        const requestPlaybackStats = message.Data?.requestPlaybackStats === true;
        this.stats.lastPing = new Date();
        this.notifyStatsUpdated();
        this.addLog(
          LogType.Info,
          `🏓 Health check received, sending pong${requestPlaybackStats ? ' with playback stats' : ''
          }...`
        );
        this.sendPong(requestPlaybackStats);
        break;

      case 'playlist':
        this.addLog(LogType.Info, `?? Playlist command: ${JSON.stringify(message.Data)}`);
        break;

      default:
        this.addLog(LogType.Info, `Unknown message type: ${message.Type}`);
    }
  }

  private sendPong(includePlaybackStats: boolean = false): void {
    const data: any = { timestamp: new Date().toISOString() };

    if (includePlaybackStats && this.getPlaybackStats) {
      const playbackStats = this.getPlaybackStats();
      if (playbackStats && Object.keys(playbackStats).length > 0) {
        data.playbackStats = playbackStats;
        this.addLog(
          LogType.Info,
          `📊 Sending playback stats: ${Object.keys(playbackStats).length} assets`
        );
      }
    }

    const pongMessage: WebSocketMessage = {
      Type: 'pong',
      Data: data,
    };
    this.sendMessage(JSON.stringify(pongMessage));
  }

  private sendMessage(message: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(message);
      this.stats.messagesSent++;
      this.notifyStatsUpdated();
    } else {
      this.addLog(LogType.Error, 'Cannot send message: not connected');
    }
  }

  private updateStatus(status: ConnectionStatus): void {
    this.status = status;
    if (this.onStatusChange) {
      this.onStatusChange(status);
    }
  }

  private addLog(type: LogType, message: string): void {
    const log: LogEntry = {
      timestamp: new Date(),
      type,
      message,
    };
    this.logs.push(log);

    if (this.onLogAdded) {
      this.onLogAdded(log);
    }
  }

  private notifyStatsUpdated(): void {
    if (this.onStatsUpdated) {
      this.onStatsUpdated(this.getStats());
    }
  }

  private resetStats(): void {
    this.stats = {
      connectionTime: null,
      messagesSent: 0,
      messagesReceived: 0,
      lastPing: null,
    };
    this.notifyStatsUpdated();
  }
}
